import React, { Component } from "react";
import {
    Segment,
    List,
    Icon,
    Label,
    Button,
    Input,
    Popup, Dimmer
} from "semantic-ui-react";
import _ from "lodash";
import { viewsItem, selectedViewsItem } from "./DataGrid.css";
import { getSavedViews, saveView, updateView, deleteViews } from '../../services/offpriceServices'

export default class ViewsToolPanel extends Component {
    constructor(props) {
        super(props);

        this.state = {
            loadingViews: false,
            creatingView: false,
            currentView: null,
            showView: props.setCurrentView,
            viewsData: [],
            editing: false,
            selectedItems: [],
            currentCols: [],
            isCurrentViewMatching: true,
            hoverIndex: null,
            worksheetType: props.worksheetType
        };

        _.bindAll(this, "fetchList", "updateCurrentCols", "isOnHover", "clearHoverIndex", "attemptCreateView",
            "removeCreateAttempt", "saveNewView", "updateCurrentView", "deleteSelectedViews", "attemptDeleteItem",
            "isDeletingView", "removeDeleteAttempt", "attemptDeleteManyItems", "isAttemptingDeleteMany",
            "removeDeleteManyAttempt");

        this.props.api.addEventListener('displayedColumnsChanged', this.updateCurrentCols);
        this.props.api.addEventListener('gridReady', this.updateCurrentCols);
    }

    componentDidMount() {
        this.fetchList();
    }

    /**
     * fetches the list, and preselects the default view when the previously selected view is no longer in the list,
     * keeps the selected view if it's still there, or shows the most recent view added when it was not there before
     * fetching
     */
    fetchList() {
        this.setState({ loadingViews: true });
        this.loadViews()
            .then(viewsData => {
                let previousView = _.chain(viewsData).find({ RowKey: _.get(this, "state.currentView.RowKey") }).value();
                let newViewsData = _.map(viewsData, "RowKey");
                let oldViewsData = _.chain(this).get("state.viewsData").map("RowKey").value();
                let newCurrentView = _.chain(viewsData)
                    .find({ RowKey: _.chain(newViewsData).difference(oldViewsData).first().value() })
                    .defaultTo(previousView).value();

                this.setState({ loadingViews: false, viewsData: viewsData });
                this.showView(newCurrentView);
            });
    }

    /**
     * fired after changing columns manually or by changing the selected view, used to compare current grid columns to
     * view definitions, and to update the current representation of the columns as a String, which is used to save
     */
    updateCurrentCols() {
        let currentCols = _.chain(this).get("props.api.columnController.allDisplayedColumns").map("colDef").map(e => e.field || e.headerName).value();
        let colsJson = _.chain(currentCols).compact().map((col, i) => ({
            fieldName: col,
            pos: i
        })).thru(v => [...v]).value();
        this.setState(() => {
            return {
                currentCols,
                isCurrentViewMatching: this.matchView(this.state.currentView, currentCols),
                colsStr: colsJson
            }
        })
    }

    /**
     * compares the columns on the passed view to the list of columns in colsArray, returning true if they match, or
     * if the columns list on the view passed is empty
     * @param view
     * @param colsArray
     * @returns {boolean|*}
     */
    matchView(view, colsArray) {
        if (_.isEmpty(_.get(view, "cols"))) return true;
        return _.chain(view).get("cols").sortBy("pos").map("fieldName")
            .map(e => _.replace(e, /_.*$/, ""))
            .isEqual(_.map(colsArray, e => _.replace(e, /_.*$/, "")))
            .value();
    }

    /**
     * fetches view info from service, returning array with names and json strings, it also sorts the views by timestamp
     * in decending order and filters out views that do not match the current worksheet type
     * @returns {Promise<*[]>}
     */
    loadViews() {
        let worksheetType = _.get(this, "state.worksheetType");
        return getSavedViews(worksheetType).then(viewsData => {
            return _.chain(viewsData)
                    .orderBy(v => new Date(_.get(v, "Timestamp")), ["desc"])
                    .map(view => ({ ...view, name: _.get(view, "ViewName"), cols: JSON.parse(_.get(view, "ViewState")) }))
                    .tap(v => console.log("VIEWS DATA: ", v))
                    .value();
            }
        );
    }

    /**
     * updates the current view on the state, and fires showView on the main grid, which in turn should trigger a full
     * update using updateCurrentCols when the new view has different cols than the previous one. just in case,
     * updateCurrentCols is still called here, to make sure to re-match views that for some reason have the same
     * column definition, this not triggering the grid change event
     * @param view
     */
    showView(view) {
        this.setState({
            currentView: { ...view },
            isCurrentViewMatching: true
        });
        this.state.showView(view)
        // this.updateCurrentCols();
    }

    /**
     * while editing the list, should append the passed view to the selectedItems list on the state if it was not there
     * already, or remove it from that list if it was already included, and while not editing, should do nothing. In either
     * case it should also turn off the delete many attempt flag
     * @param view
     */
    toggleViewSelection(view) {
        if (_.eq(view.RowKey, "default")) return;
        if (!_.get(this, "state.editing")) return;
        this.setState(state => {
            let isSelected = this.isViewChecked(view.RowKey);
            return {
                selectedItems: _.uniq(isSelected ? _.reject(state.selectedItems, e => _.eq(e, view.RowKey)) : [...state.selectedItems, view.RowKey]),
                attemptDeleteMany: false
            }
        })
    }

    /**
     * returns true if the passed row key is already included in the selectedItems list on the state
     * @param RowKey
     * @returns {*}
     */
    isViewChecked = (RowKey) => {
        return _.chain(this).get("state.selectedItems").includes(RowKey).value()
    };

    /**
     * toggles editing state and removes any items from the selectedItems list on the state
     */
    toggleEditing = () => {
        this.setState(state => {
            return {
                editing: !_.get(state, "editing"),
                selectedItems: []
            }
        })
    };

    /**
     * updates the item on hover, which is used to display the delete button specific to that view
     * @param index
     */
    onHover(index) {
        this.setState({ hoverIndex: index })
    }

    /**
     * checks whether the passed index is on hover or not, returning true if it is. For the default view, index 0, always
     * returns false
     * @param index
     * @returns {boolean|*}
     */
    isOnHover(index) {
        if (_.eq(index, 0)) return false;
        return _.chain(this).get("state.hoverIndex").eq(index).value()
    }

    /**
     * removes the hover index, used when the cursor leaves the views list
     */
    clearHoverIndex() {
        this.setState({ hoverIndex: null })
    }

    /**
     * turns on the delete many flag
     */
    attemptDeleteManyItems() {
        this.setState({
            attemptDeleteMany: true
        })
    }

    /**
     * returns true when the delete many flag is true
     * @returns {boolean}
     */
    isAttemptingDeleteMany() {
        return !!_.get(this, "state.attemptDeleteMany");
    }

    /**
     * turns off the delete many flag
     */
    removeDeleteManyAttempt() {
        this.setState({
            attemptDeleteMany: false
        })
    }

    /**
     * turns on the delete item flag specifying which item is being deleted
     * @param view
     */
    attemptDeleteItem(view) {
        this.setState({
            attemptDeleteView: view
        })
    }

    /**
     * resets the delete item flag
     */
    removeDeleteAttempt() {
        this.setState({
            attemptDeleteView: null
        })
    }

    /**
     * checks whether the passed view matches the one being deleted in the delete item flag, returns false if the passed
     * view doesn't have a RowKey
     * @param view
     * @returns {boolean|*}
     */
    isDeletingView(view) {
        if (_.isNil(_.get(view, "RowKey"))) return false;
        return _.chain(this).get("state.attemptDeleteView.RowKey").eq(_.get(view, "RowKey")).value();
    }

    /**
     * calls the deleteView endpoint with the passed RowKey
     * @param rowKey
     * @returns {*}
     */
    deleteItem(rowKey) {
        this.removeDeleteAttempt();
        return deleteViews([rowKey])
            .then(this.fetchList);
    }

    /**
     * calls the deleteView endpoint passing the RowKeys of all the items in the selectedItems list
     * @returns {*}
     */
    deleteSelectedViews() {
        let rowKeys = _.chain(this).get("state.viewsData")
            .filter(v => _.includes(_.get(this, "state.selectedItems"), _.get(v, "RowKey")))
            .map("RowKey").value();
        this.removeDeleteManyAttempt();
        return deleteViews(rowKeys)
            .then(() => this.setState({ editing: false }))
            .then(this.fetchList);
    }

    /**
     * sets the creatingView flag to true
     */
    attemptCreateView() {
        this.setState({ creatingView: true })
    }

    /**
     * sets the creatingView flag off, and removes any create errors that could have been set in the process
     */
    removeCreateAttempt() {
        this.setState({ creatingView: false, createError: null })
    }

    /**
     * calls the saveView endpoint passing the name typed by the user, colsString updated on updateCurrentCols and
     * current worksheet type. If name is either empty or already exists for another view, a create error is set in the
     * state instead of calling the endpoint.
     * @returns {void|*}
     */
    saveNewView() {
        let name = _.get(this, "refs.newViewNameInput.inputRef.current.value", "");
        let colsString = _.get(this, "state.colsStr");
        let worksheetType = _.get(this, "state.worksheetType");

        if (_.eq(name, "") || !name) {
            return this.setState({
                createError: "name cannot be empty"
            })
        }

        if (_.chain(this).get("state.viewsData").map("name").includes(name).value()) {
            return this.setState({
                createError: `view "${name}" already exists`
            })
        }

        this.removeCreateAttempt();

        return saveView(name, colsString, worksheetType)
            .then(() => this.setState({ createError: null }))
            .then(this.fetchList);
    }

    /**
     * calls the updateView endpoint, passing the current view's RowKey plus the new colString updated on
     * updateCurrentCols
     * @returns {*}
     */
    updateCurrentView() {
        let name = _.get(this, "state.currentView.name");
        let rowKey = _.get(this, "state.currentView.RowKey");
        let colsString = _.get(this, "state.colsStr");

        return updateView(rowKey, name, colsString)
            .then(this.removeCreateAttempt)
            .then(this.fetchList);
    }

    render() {
        let loading = _.get(this, "state.loadingViews");
        let defaultView = { name: "default view", cols: null, RowKey: "default" };
        let viewsArray = _.union([defaultView], this.state.viewsData);
        let activeKey = _.chain(viewsArray).find({ RowKey: _.get(this, "state.currentView.RowKey") }).defaultTo(defaultView).get("RowKey").value();
        let editing = !!_.get(this, "state.editing");
        let selectionCount = _.get(this, "state.selectedItems.length");
        let isCurrentViewMatching = _.get(this, "state.isCurrentViewMatching");
        let isDefaultView = _.eq(activeKey, "default");
        let createError = _.get(this, "state.createError");
        let creatingView = !!_.get(this, "state.creatingView");

        return (<div style={{ margin: "10px" }}>
                <Dimmer.Dimmable dimmed={loading} as={Segment} style={{ position: "relative" }} onMouseOut={this.clearHoverIndex}>
                    <Dimmer active={loading} inverted/>

                    <h3>Views</h3>
                    <Label style={{ position: "absolute", top: "10px", right: "10px" }} as="a" circular
                           onClick={this.toggleEditing}>
                        <Icon name="edit outline" fitted/>
                    </Label>
                    {!editing && <Label style={{ position: "absolute", top: "10px", right: "40px" }} as="a" circular
                                        onClick={this.fetchList}>
                        <Icon name="refresh" fitted/>
                    </Label>}

                        <List selection verticalAlign='middle'>
                            {
                                _.map(viewsArray, (e, i) => <List.Item
                                    key={`viewitem_${e.RowKey}`}
                                    active={e.RowKey === activeKey}
                                    as={editing ? "a" : "span"}
                                    onMouseOver={_.bind(this.onHover, this, i)}
                                    onClick={editing ? _.bind(this.toggleViewSelection, this, e) : _.bind(this.showView, this, e)}
                                >
                                    {editing
                                        ? <Label color={this.isViewChecked(e.RowKey) ? 'blue' : null}>{e.name}</Label>
                                        : <div className={e.RowKey === activeKey ? selectedViewsItem : viewsItem}>
                                            <span>{`${e.name} ${!isCurrentViewMatching && e.RowKey === activeKey ? " *" : ""}`}</span>
                                            {
                                                this.isDeletingView(e)
                                                    ? <Button.Group size="mini">
                                                        <Button compact icon onClick={evt => {
                                                            evt.stopPropagation();
                                                            return this.deleteItem([_.get(e, "RowKey")])
                                                        }}>confirm</Button>
                                                        <Button negative compact icon onClick={this.removeDeleteAttempt}>
                                                            <Icon name="cancel"/>
                                                        </Button>
                                                    </Button.Group>
                                                    : <Label as="a" circular
                                                             style={{ opacity: this.isOnHover(i) ? 1 : 0 }}
                                                             onClick={evt => {
                                                                 evt.stopPropagation();
                                                                 return this.attemptDeleteItem(e);
                                                                 //return this.deleteItem([_.get(e, "RowKey")])
                                                             }}>
                                                        <Icon name="delete" fitted/>
                                                    </Label>
                                            }
                                        </div>
                                    }
                                </List.Item>)
                            }
                        </List>
                </Dimmer.Dimmable>
                {editing &&
                <Segment>
                    {
                        this.isAttemptingDeleteMany()
                            ? <Button.Group fluid>
                                <Button icon onClick={this.deleteSelectedViews}>
                                    {_.eq(selectionCount, 0) ? "confirm" : `confirm (${selectionCount})`}
                                </Button>
                                <Button negative icon onClick={this.removeDeleteManyAttempt}>
                                    <Icon name="cancel"/>
                                </Button>
                            </Button.Group>
                            : <Button basic fluid color="red" disabled={_.eq(selectionCount, 0)}
                                      onClick={this.attemptDeleteManyItems}>
                                {_.eq(selectionCount, 0) ? "delete" : `delete ${selectionCount} views`}
                            </Button>
                    }
                </Segment>
                }
                <Segment>
                    {creatingView
                        ? <Popup
                            open={!_.isEmpty(createError)}
                            content={createError}
                            trigger={
                                <Input fluid placeholder="view name..." ref="newViewNameInput" action>
                                    <input onKeyUp={evt => {
                                        if (_.eq(evt.keyCode, 13)) this.saveNewView()
                                    }}/>
                                    <Button negative icon onClick={this.removeCreateAttempt}><Icon fitted size="small"
                                                                                                   name="cancel"/></Button>
                                    <Button positive icon onClick={this.saveNewView}><Icon fitted size="small"
                                                                                           name="checkmark"/></Button>
                                </Input>
                            }
                        >

                        </Popup>
                        : <Button fluid disabled={editing} onClick={this.attemptCreateView}>add new view</Button>
                    }
                </Segment>
                <Segment>
                    <Button fluid disabled={editing || isCurrentViewMatching || isDefaultView}
                            onClick={this.updateCurrentView}
                    >update current view</Button>
                </Segment>
            </div>
        )
    }
}



// WEBPACK FOOTER //
// ./components/product-detail/ViewsToolPanel.js